import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import AgendaCheckbox from './AgendaCheckbox';
import { readableZeroedHour } from '../../utils/reparation';
import AngleRight16 from '../icons/AngleRight16';
import Badge from '../shared/atoms/Badge';
import Text from '../shared/atoms/Text';
import Box from '../shared/atoms/Box';

const CardContainer = styled.div`
  width: 100%;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
`;

const CardHeader = styled.button`
  width: 100%;
  background: white;
  border: none;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'LTSaeada', Arial, sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #5b5ff5;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

const DayName = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
  text-transform: capitalize;
`;

const DateText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const SlotCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountBadge = styled.div`
  background: ${(props) => (props.hasSlots ? '#d5ff4f' : '#f3f4f6')};
  color: ${(props) => (props.hasSlots ? '#1f2937' : '#9ca3af')};
  font-size: 0.875rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 12px;
`;

const SelectedBadge = styled.div`
  background: #5b5ff5;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 10px;
`;

const ExpandIcon = styled(AngleRight16)`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
  margin-left: 8px;
  color: #6b7280;
`;

const ExpandedContent = styled.div`
  padding: 16px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-top: 12px;
  margin-bottom: 8px;
  text-transform: uppercase;

  &:first-child {
    margin-top: 0;
  }
`;

const SelectAllButton = styled.button`
  background: ${(props) => (props.active ? '#5b5ff5' : 'white')};
  color: ${(props) => (props.active ? 'white' : '#5b5ff5')};
  border: 2px solid #5b5ff5;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  width: 100%;

  &:hover {
    background: ${(props) => (props.active ? '#4a4ae5' : '#f9fafb')};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ExpandableDayCard = ({
  day,
  slots,
  selectedCount,
  toggleSlot,
  averageOptiv,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dayMoment = moment(day.date);
  const dayName = dayMoment.format('dddd');
  const dateStr = dayMoment.format('DD MMMM');

  const availableSlotsArray = slots.filter((s) => !s.full);
  const availableSlotsCount = availableSlotsArray.length;
  const hasSlots = availableSlotsCount > 0;

  const amSlots = slots.filter((slot) => slot.min <= 13);
  const pmSlots = slots.filter((slot) => slot.min >= 14);

  const handleSlotClick = (slot) => {
    if (slot.full) return;
    toggleSlot(
      dayMoment.set({ hour: slot.min }).format('YYYY-MM-DD HH:00:00'),
      dayMoment.set({ hour: slot.max }).format('YYYY-MM-DD HH:00:00'),
    );
  };

  const handleSelectAll = () => {
    const allSelected = availableSlotsArray.every((s) => s.checked);

    if (allSelected) {
      // Désélectionner tous les créneaux
      availableSlotsArray.forEach((slot) => {
        if (slot.checked) {
          handleSlotClick(slot);
        }
      });
    } else {
      // Sélectionner tous les créneaux disponibles
      availableSlotsArray.forEach((slot) => {
        if (!slot.checked) {
          handleSlotClick(slot);
        }
      });
    }
  };

  const allSelected =
    availableSlotsCount > 0 && availableSlotsArray.every((s) => s.checked);

  return (
    <CardContainer>
      <CardHeader onClick={() => setIsOpen(!isOpen)}>
        <LeftSection>
          <DayName>{dayName}</DayName>
          <DateText>{dateStr}</DateText>
        </LeftSection>
        <RightSection>
          <SlotCount>
            <CountBadge hasSlots={hasSlots}>
              {availableSlotsCount} créneau{availableSlotsCount > 1 ? 'x' : ''}
            </CountBadge>
            <ExpandIcon isOpen={isOpen} />
          </SlotCount>
          {selectedCount > 0 && (
            <SelectedBadge>
              {selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}
            </SelectedBadge>
          )}
        </RightSection>
      </CardHeader>

      {isOpen && (
        <ExpandedContent>
          <SelectAllButton onClick={handleSelectAll} active={allSelected}>
            {allSelected ? 'Tout désélectionner' : 'Toute la journée'}
          </SelectAllButton>

          {amSlots.length > 0 && (
            <div>
              <SectionTitle>Matin</SectionTitle>
              <SlotsGrid>
                {amSlots.map((slot) => (
                  <AgendaCheckbox
                    key={`${slot.min}${slot.max}`}
                    mostAvailabilities={
                      slot.averageAvailabilities >= averageOptiv
                    }
                    yieldAmount={slot.yieldAmount}
                    disabled={slot.full}
                    className="dateTimeModule-checkbox"
                    text={
                      slot.full ? (
                        <small>Complet</small>
                      ) : (
                        `${readableZeroedHour(slot.min)}-${readableZeroedHour(
                          slot.max,
                        )}`
                      )
                    }
                    onClick={() => handleSlotClick(slot)}
                    value={slot.checked}
                  />
                ))}
              </SlotsGrid>
            </div>
          )}

          {pmSlots.length > 0 && (
            <div>
              <SectionTitle>Après-midi</SectionTitle>
              <SlotsGrid>
                {pmSlots.map((slot) => (
                  <AgendaCheckbox
                    key={`${slot.min}${slot.max}`}
                    mostAvailabilities={
                      slot.averageAvailabilities >= averageOptiv
                    }
                    yieldAmount={slot.yieldAmount}
                    disabled={slot.full}
                    className="dateTimeModule-checkbox"
                    text={
                      slot.full ? (
                        <small>Complet</small>
                      ) : (
                        `${readableZeroedHour(slot.min)}-${readableZeroedHour(
                          slot.max,
                        )}`
                      )
                    }
                    onClick={() => handleSlotClick(slot)}
                    value={slot.checked}
                  />
                ))}
              </SlotsGrid>
            </div>
          )}

          {amSlots.length === 0 && pmSlots.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                color: '#9ca3af',
                padding: '16px',
              }}>
              Aucun créneau disponible pour ce jour
            </div>
          )}
        </ExpandedContent>
      )}
    </CardContainer>
  );
};

ExpandableDayCard.propTypes = {
  day: PropTypes.object.isRequired,
  slots: PropTypes.array.isRequired,
  selectedCount: PropTypes.number.isRequired,
  toggleSlot: PropTypes.func.isRequired,
  averageOptiv: PropTypes.number,
};

export default ExpandableDayCard;
